import { Point } from 'pixi.js';
import { GridSystem, GridObject } from '@gameplay/building/GridSystem';
import { RoomCategory, RoomDefinition, RoomTile, ServiceLocation } from './RoomTypes';

export class RoomManager {
  private readonly gridSystem: GridSystem;

  private rooms: Map<string, RoomDefinition> = new Map();
  private tileToRoom: Map<string, string> = new Map();
  private services: Map<string, ServiceLocation> = new Map();
  private roomServiceKeys: Map<string, string> = new Map();

  constructor(gridSystem: GridSystem) {
    this.gridSystem = gridSystem;
  }

  public clear(): void {
    this.rooms.clear();
    this.tileToRoom.clear();
    this.services.clear();
    this.roomServiceKeys.clear();
  }

  public registerService(location: ServiceLocation): void {
    this.services.set(location.id, location);
  }

  public unregisterService(id: string): void {
    this.services.delete(id);
  }

  public recordPlacement(object: GridObject): void {
    const tileKey = this.getTileKey(object.gridX, object.gridY, object.floor);
    if (this.tileToRoom.has(tileKey)) {
      return;
    }

    const neighboringRooms = this.getNeighborRoomIds(object.gridX, object.gridY, object.floor);

    if (neighboringRooms.length === 0) {
      const roomId = this.createRoom([{
        gridX: object.gridX,
        gridY: object.gridY,
        floor: object.floor
      }]);
      this.tileToRoom.set(tileKey, roomId);
      return;
    }

    const primaryRoomId = neighboringRooms[0];
    const primaryRoom = this.rooms.get(primaryRoomId);

    if (!primaryRoom) {
      return;
    }

    primaryRoom.tiles.push({ gridX: object.gridX, gridY: object.gridY, floor: object.floor });
    this.tileToRoom.set(tileKey, primaryRoomId);

    if (neighboringRooms.length > 1) {
      this.mergeRooms(primaryRoomId, neighboringRooms.slice(1));
      const updatedRoom = this.rooms.get(primaryRoomId);
      if (updatedRoom) {
        this.updateRoomService(updatedRoom);
      }
    } else {
      this.updateRoomService(primaryRoom);
    }
  }

  public removeTile(gridX: number, gridY: number, floor: number): RoomDefinition[] {
    const affected: RoomDefinition[] = [];
    const tileKey = this.getTileKey(gridX, gridY, floor);
    const roomId = this.tileToRoom.get(tileKey);
    if (!roomId) {
      return affected;
    }

    this.tileToRoom.delete(tileKey);
    const room = this.rooms.get(roomId);
    if (!room) {
      return affected;
    }

    room.tiles = room.tiles.filter((tile) => !(tile.gridX === gridX && tile.gridY === gridY && tile.floor === floor));

    if (room.tiles.length === 0) {
      this.removeRoom(roomId);
      return affected;
    }

    // Re-scan the room's tiles to identify contiguous regions
    const clusters = this.findTileClusters(room.tiles);
    if (clusters.length <= 1) {
      this.updateRoomService(room);
      affected.push(room);
      return affected;
    }

    // Reassign clusters to existing/new rooms
    const originalCategory = room.category;
    const originalQuality = room.quality;

    this.removeRoom(roomId);

    clusters.forEach((clusterTiles) => {
      const newRoomId = this.createRoom(clusterTiles, originalCategory);
      const newRoom = this.rooms.get(newRoomId)!;
      newRoom.quality = originalQuality;
      clusterTiles.forEach((tile) => {
        this.tileToRoom.set(this.getTileKey(tile.gridX, tile.gridY, tile.floor), newRoomId);
      });
      affected.push(newRoom);
    });

    return affected;
  }

  public getRoomAt(gridX: number, gridY: number, floor: number): RoomDefinition | undefined {
    const key = this.getTileKey(gridX, gridY, floor);
    const roomId = this.tileToRoom.get(key);
    if (!roomId) {
      return undefined;
    }
    return this.rooms.get(roomId);
  }

  public getRooms(): RoomDefinition[] {
    return Array.from(this.rooms.values());
  }

  public getRoom(roomId: string): RoomDefinition | undefined {
    return this.rooms.get(roomId);
  }

  public getServicesByCategory(category: RoomCategory): ServiceLocation[] {
    return Array.from(this.services.values()).filter((service) => service.category === category);
  }

  public updateRoomQuality(roomId: string, quality: number): void {
    const room = this.rooms.get(roomId);
    if (!room) {
      return;
    }
    room.quality = quality;
  }

  public recordFloorPlacement(objects: GridObject[]): void {
    objects.forEach((object) => this.recordPlacement(object));
  }

  public setRoomCategory(roomId: string, category: RoomCategory): RoomDefinition | undefined {
    const room = this.rooms.get(roomId);
    if (!room) {
      return undefined;
    }

    room.category = category;
    this.updateRoomService(room);
    return room;
  }

  /**
   * Assign a category to a specific tile. If the tile belongs to an existing room and the
   * category differs from that room's category, the tile is detached from the room and a
   * new single-tile room with the target category is created. This yields per-tile categorization.
   */
  public setTileCategory(gridX: number, gridY: number, floor: number, category: RoomCategory): void {
    const key = this.getTileKey(gridX, gridY, floor);
    const existingRoomId = this.tileToRoom.get(key);

    if (!existingRoomId) {
      // No room yet for this tile; create a new single-tile room
      const newRoomId = this.createRoom([{ gridX, gridY, floor }], category);
      this.tileToRoom.set(key, newRoomId);
      return;
    }

    const existingRoom = this.rooms.get(existingRoomId);
    if (!existingRoom) return;

    if (existingRoom.category === category) {
      // Nothing to do; already desired category
      return;
    }

    // Remove tile from existing room
    existingRoom.tiles = existingRoom.tiles.filter(t => !(t.gridX === gridX && t.gridY === gridY && t.floor === floor));
    this.tileToRoom.delete(key);

    // If the existing room split into multiple clusters, re-cluster
    if (existingRoom.tiles.length === 0) {
      this.removeRoom(existingRoomId);
    } else {
      const clusters = this.findTileClusters(existingRoom.tiles);
      // Remove original room and recreate clusters preserving original category
      const originalCategory = existingRoom.category;
      this.removeRoom(existingRoomId);
      clusters.forEach(cluster => {
        const newId = this.createRoom(cluster, originalCategory);
        cluster.forEach(tile => this.tileToRoom.set(this.getTileKey(tile.gridX, tile.gridY, tile.floor), newId));
      });
    }

    // Create a new room for the single tile with the requested category
    const singleRoomId = this.createRoom([{ gridX, gridY, floor }], category);
    this.tileToRoom.set(key, singleRoomId);
  }

  private updateRoomService(room: RoomDefinition): void {
    const previousServiceKey = this.roomServiceKeys.get(room.id);
    if (previousServiceKey) {
      this.services.delete(previousServiceKey);
      this.roomServiceKeys.delete(room.id);
    }

    if (this.isServiceCategory(room.category)) {
      const centroid = this.getRoomCentroid(room.tiles);
      const serviceKey = this.getServiceKey(room.id, room.category);
      const service: ServiceLocation = {
        id: serviceKey,
        category: room.category,
        gridX: centroid.x,
        gridY: centroid.y,
        floor: centroid.floor
      };
      this.services.set(serviceKey, service);
      this.roomServiceKeys.set(room.id, serviceKey);
    }
  }

  private getRoomCentroid(tiles: RoomTile[]): { x: number; y: number; floor: number } {
    const sum = tiles.reduce(
      (acc, tile) => {
        acc.x += tile.gridX;
        acc.y += tile.gridY;
        acc.floor += tile.floor;
        return acc;
      },
      { x: 0, y: 0, floor: 0 }
    );

    const count = Math.max(tiles.length, 1);

    return {
      x: Math.round(sum.x / count),
      y: Math.round(sum.y / count),
      floor: Math.round(sum.floor / count)
    };
  }

  private isServiceCategory(category: RoomCategory): boolean {
    return ['dining', 'medical', 'bathroom', 'recreation', 'utility'].includes(category);
  }

  private getServiceKey(roomId: string, category: RoomCategory): string {
    return `service-${category}-${roomId}`;
  }

  private createRoom(tiles: RoomTile[], category: RoomCategory = 'unspecified'): string {
    const roomId = `room-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const room: RoomDefinition = {
      id: roomId,
      category,
      tiles: [...tiles],
      quality: 0.5
    };
    this.rooms.set(roomId, room);
    this.updateRoomService(room);
    return roomId;
  }

  private removeRoom(roomId: string): void {
    this.rooms.delete(roomId);
    const serviceKey = this.roomServiceKeys.get(roomId);
    if (serviceKey) {
      this.services.delete(serviceKey);
      this.roomServiceKeys.delete(roomId);
    }
  }

  private getNeighborRoomIds(gridX: number, gridY: number, floor: number): string[] {
    const neighbors: string[] = [];

    const directions = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 }
    ];

    directions.forEach((dir) => {
      const key = this.getTileKey(gridX + dir.x, gridY + dir.y, floor);
      const roomId = this.tileToRoom.get(key);
      if (roomId && !neighbors.includes(roomId)) {
        neighbors.push(roomId);
      }
    });

    return neighbors;
  }

  private mergeRooms(targetRoomId: string, roomIdsToMerge: string[]): void {
    const targetRoom = this.rooms.get(targetRoomId);
    if (!targetRoom) {
      return;
    }

    roomIdsToMerge.forEach((roomId) => {
      const room = this.rooms.get(roomId);
      if (!room) {
        return;
      }

      room.tiles.forEach((tile) => {
        targetRoom.tiles.push(tile);
        this.tileToRoom.set(this.getTileKey(tile.gridX, tile.gridY, tile.floor), targetRoomId);
      });

      this.removeRoom(roomId);
    });

    this.updateRoomService(targetRoom);
  }

  private findTileClusters(tiles: RoomTile[]): RoomTile[][] {
    const clusters: RoomTile[][] = [];
    const visited = new Set<string>();

    const tileMap = new Map<string, RoomTile>();
    tiles.forEach((tile) => tileMap.set(this.getTileKey(tile.gridX, tile.gridY, tile.floor), tile));

    const directions = [
      { x: 1, y: 0 },
      { x: -1, y: 0 },
      { x: 0, y: 1 },
      { x: 0, y: -1 }
    ];

    tileMap.forEach((tile) => {
      const key = this.getTileKey(tile.gridX, tile.gridY, tile.floor);
      if (visited.has(key)) {
        return;
      }

      const queue: RoomTile[] = [tile];
      const cluster: RoomTile[] = [];
      visited.add(key);

      while (queue.length > 0) {
        const current = queue.shift()!;
        cluster.push(current);

        directions.forEach((dir) => {
          const neighborKey = this.getTileKey(
            current.gridX + dir.x,
            current.gridY + dir.y,
            current.floor
          );
          const neighborTile = tileMap.get(neighborKey);
          if (neighborTile && !visited.has(neighborKey)) {
            visited.add(neighborKey);
            queue.push(neighborTile);
          }
        });
      }

      clusters.push(cluster);
    });

    return clusters;
  }

  private getTileKey(gridX: number, gridY: number, floor: number): string {
    return `${floor}:${gridX}:${gridY}`;
  }
}
